import React, { useEffect } from "react";
import { Button, ButtonGroup } from "react-bootstrap";

enum ContextMenuOperation {
  Separator,
  CopyRow,
  CopyCell,
  ClearCell,
  PasteRow,
  PasteCell,
  PasteCells,
  DeleteRow,
  NumberPlate,
  Vin,
  QrCode,
  HsnTsn,
  Dat,
}

export class ContextMenuOption {
  constructor(
    readonly operation: ContextMenuOperation,
    readonly text: string,
  ) {}
}

export const AvailableOptions = {
  separator: new ContextMenuOption(ContextMenuOperation.Separator, ""),
  copyRows: new ContextMenuOption(ContextMenuOperation.CopyRow, "Copy Rows"),
  copyRow: new ContextMenuOption(ContextMenuOperation.CopyRow, "Copy Row"),
  copyCell: new ContextMenuOption(ContextMenuOperation.CopyCell, "Copy Cell"),
  clearCell: new ContextMenuOption(
    ContextMenuOperation.ClearCell,
    "Clear Cell",
  ),
  clearCells: new ContextMenuOption(
    ContextMenuOperation.ClearCell,
    "Clear Cells",
  ),
  pasteRows: new ContextMenuOption(ContextMenuOperation.PasteRow, "Paste Rows"),
  pasteCell: new ContextMenuOption(
    ContextMenuOperation.PasteCell,
    "Paste Cell",
  ),
  pasteCells: new ContextMenuOption(
    ContextMenuOperation.PasteCells,
    "Paste Cells",
  ),
  pasteRow: new ContextMenuOption(ContextMenuOperation.PasteRow, "Paste Row"),
  deleteRow: new ContextMenuOption(
    ContextMenuOperation.DeleteRow,
    "Mark for Deletion",
  ),
  undeleteRow: new ContextMenuOption(
    ContextMenuOperation.DeleteRow,
    "Unmark for Deletion",
  ),
  viewDatNumbers: new ContextMenuOption(
    ContextMenuOperation.Dat,
    "View DAT Numbers",
  ),
  viewQrCodes: new ContextMenuOption(
    ContextMenuOperation.QrCode,
    "View QR Codes",
  ),
  viewVins: new ContextMenuOption(ContextMenuOperation.Vin, "View VINs"),
  viewNumberPlates: new ContextMenuOption(
    ContextMenuOperation.NumberPlate,
    "View Number Plates",
  ),
  viewHsnTsn: new ContextMenuOption(
    ContextMenuOperation.HsnTsn,
    "View HSN/TSN",
  ),
};

interface ModelInfoContextMenuProps {
  onClose: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onCopyCell: () => void;
  onPasteCell: () => void;
  onPasteCells: () => void;
  onAddNumberPlate: () => void;
  onAddVin: () => void;
  onAddQrCode: () => void;
  onAddHsnTsn: () => void;
  onMarkForDeletion: () => void;
  onViewDatNumbers: () => void;
  onClearCell: () => void;
  options: ContextMenuOption[];
  style?: React.CSSProperties;
}

const ModelInfoContextMenu: React.FC<ModelInfoContextMenuProps> = ({
  onClose,
  onCopy,
  onPaste,
  onCopyCell,
  onClearCell,
  onPasteCell,
  onPasteCells,
  onAddNumberPlate,
  onAddVin,
  onAddQrCode,
  onAddHsnTsn,
  onMarkForDeletion,
  onViewDatNumbers,
  options,
  style,
}) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !event.target ||
        !(event.target as HTMLElement).closest(".context-menu")
      ) {
        onClose();
      }
    };

    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [onClose]);

  const handleOptionSelect = (operation: ContextMenuOperation) => {
    switch (operation) {
      case ContextMenuOperation.CopyRow:
        onCopy();
        break;
      case ContextMenuOperation.PasteRow:
        onPaste();
        break;
      case ContextMenuOperation.CopyCell:
        onCopyCell();
        break;
      case ContextMenuOperation.ClearCell:
        onClearCell();
        break;
      case ContextMenuOperation.PasteCell:
        onPasteCell();
        break;
      case ContextMenuOperation.PasteCells:
        onPasteCells();
        break;
      case ContextMenuOperation.NumberPlate:
        onAddNumberPlate();
        break;
      case ContextMenuOperation.Vin:
        onAddVin();
        break;
      case ContextMenuOperation.QrCode:
        onAddQrCode();
        break;
      case ContextMenuOperation.HsnTsn:
        onAddHsnTsn();
        break;
      case ContextMenuOperation.DeleteRow:
        onMarkForDeletion();
        break;
      case ContextMenuOperation.Dat:
        onViewDatNumbers();
        break;
      default:
        return;
    }

    // close if it was handled
    onClose();
  };

  return (
    <ButtonGroup vertical style={style} className="context-menu">
      {options.map((option, key) =>
        option.operation !== ContextMenuOperation.Separator ? (
          <Button
            key={key}
            variant="secondary"
            onClick={() => handleOptionSelect(option.operation)}
          >
            {option.text}
          </Button>
        ) : (
          <div
            key={key}
            style={{ padding: "1px", backgroundColor: "white", width: "100%" }}
          />
        ),
      )}
    </ButtonGroup>
  );
};

export default ModelInfoContextMenu;
